/**
 * Exact decimal-string formatting for money, token amounts and percentages.
 * Wire values are decimal strings (USD with ≤ 6 fraction digits, wei as
 * integers); nothing here goes through floating point.
 */

export interface Fixed {
  neg: boolean
  /** absolute value scaled by 10^scale */
  abs: bigint
  scale: number
}

const DEC = /^(-)?(\d+)(?:\.(\d+))?$/

export function parseDecimal(value: string): Fixed | null {
  const m = DEC.exec(value.trim())
  if (!m) return null
  const frac = m[3] ?? ''
  const abs = BigInt((m[2] ?? '0') + frac)
  return { neg: m[1] === '-' && abs !== 0n, abs, scale: frac.length }
}

/** Re-scale with round-half-away-from-zero. */
export function rescale(f: Fixed, scale: number): Fixed {
  if (scale === f.scale) return f
  if (scale > f.scale) return { ...f, abs: f.abs * 10n ** BigInt(scale - f.scale), scale }
  const div = 10n ** BigInt(f.scale - scale)
  const q = f.abs / div
  const r = f.abs % div
  const abs = r * 2n >= div ? q + 1n : q
  return { neg: f.neg && abs !== 0n, abs, scale }
}

export function isZero(f: Fixed): boolean {
  return f.abs === 0n
}

export function compareFixed(a: Fixed, b: Fixed): number {
  const s = Math.max(a.scale, b.scale)
  const x = rescale(a, s)
  const y = rescale(b, s)
  const vx = x.neg ? -x.abs : x.abs
  const vy = y.neg ? -y.abs : y.abs
  return vx === vy ? 0 : vx < vy ? -1 : 1
}

function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** Digits of `f` as `int[.frac]` without sign, grouped. */
export function digits(f: Fixed, opts: { group?: boolean; trimZeros?: boolean } = {}): string {
  const s = f.abs.toString().padStart(f.scale + 1, '0')
  let int = f.scale ? s.slice(0, -f.scale) : s
  let frac = f.scale ? s.slice(-f.scale) : ''
  if (opts.trimZeros) frac = frac.replace(/0+$/, '')
  if (opts.group !== false) int = group(int)
  return frac ? `${int}.${frac}` : int
}

export type SignMode = 'auto' | 'always' | 'never'

function signOf(f: Fixed, mode: SignMode): string {
  if (mode === 'never') return ''
  if (f.neg) return '−' // U+2212 minus: same width as plus in tabular fonts
  if (mode === 'always' && f.abs !== 0n) return '+'
  return ''
}

const UNITS: [bigint, string][] = [
  [10n ** 12n, 'T'],
  [10n ** 9n, 'B'],
  [10n ** 6n, 'M'],
  [10n ** 3n, 'k'],
]

/** Compact digits: 1840.12 → "1.84k", 421900 → "421.9k", 999.5 → "999.50" (below 1k keeps `precision`). */
export function compactDigits(f: Fixed, precision: number): string {
  const whole = f.abs / 10n ** BigInt(f.scale)
  for (const [unit, suffix] of UNITS) {
    if (whole >= unit) {
      // 3 significant digits
      const intDigits = (whole / unit).toString().length
      const places = Math.max(0, 3 - intDigits)
      const scaled = rescale({ neg: false, abs: f.abs, scale: f.scale + unit.toString().length - 1 }, places)
      let out = digits(scaled, { trimZeros: true })
      // rounding up can carry into the next unit (999.95k → 1000k); good enough, still exact in tooltip
      if (out === '1,000' && suffix !== 'T') out = '1000'
      return `${out}${suffix}`
    }
  }
  return digits(rescale(f, precision))
}

export interface FormatOptions {
  precision?: number
  compact?: boolean
  sign?: SignMode
}

/** `"-820.330000"` → `"−$820.33"`; compact `"421900"` → `"$421.9k"`. Invalid input → "—". */
export function formatUsd(value: string | null | undefined, opts: FormatOptions = {}): string {
  if (value == null) return '—'
  const f = parseDecimal(value)
  if (!f) return '—'
  const precision = opts.precision ?? 2
  const rounded = rescale(f, precision)
  const body = opts.compact ? compactDigits(f, precision) : digits(rounded)
  // a value that rounds to zero shows no sign
  return `${signOf(rounded, opts.sign ?? 'auto')}$${body}`
}

/**
 * An ETH decimal string (the platform's P&L, an exact decimal of its wei) → `"−0.2500 ETH"`: `precision` fraction
 * digits (4 by default), a value that rounds to zero shows no sign. Invalid input → "—".
 */
export function formatEth(value: string | null | undefined, opts: FormatOptions = {}): string {
  if (value == null) return '—'
  const f = parseDecimal(value)
  if (!f) return '—'
  const rounded = rescale(f, opts.precision ?? 4)
  const body = opts.compact ? compactDigits(f, opts.precision ?? 4) : digits(rounded)
  return `${signOf(rounded, opts.sign ?? 'auto')}${body} ETH`
}

/** Exact ETH value for tooltips: every digit of the wei. */
export function exactEth(value: string): string {
  const f = parseDecimal(value)
  if (!f) return value
  return `${f.neg ? '−' : ''}${digits(f)} ETH`
}

/** Exact USD value for tooltips: every stored digit, grouped. */
export function exactUsd(value: string): string {
  const f = parseDecimal(value)
  if (!f) return value
  return `${f.neg ? '−' : ''}$${digits(f)}`
}

/** Integer wei string with `decimals` → decimal Fixed. */
export function fromUnits(wei: string, decimals: number): Fixed | null {
  if (!/^-?\d+$/.test(wei)) return null
  const neg = wei.startsWith('-')
  const abs = BigInt(neg ? wei.slice(1) : wei)
  return { neg: neg && abs !== 0n, abs, scale: decimals }
}

/**
 * Token amount for display. Precision adapts to magnitude unless given:
 * ≥ 1000 → 0-2 places (or compact), ≥ 1 → 4, < 1 → up to 6 significant.
 */
export function formatTokenAmount(wei: string, decimals: number, opts: FormatOptions = {}): string {
  const f = fromUnits(wei, decimals)
  if (!f) return '—'
  const whole = f.abs / 10n ** BigInt(f.scale)
  if (opts.compact && whole >= 10_000n) return `${signOf(f, opts.sign ?? 'auto')}${compactDigits(f, 2)}`
  let precision = opts.precision
  if (precision === undefined) {
    if (whole >= 1000n) precision = 2
    else if (whole >= 1n) precision = 4
    else {
      // 4 significant digits after the leading zeros; below 1e-8 show a floor
      const frac = f.abs.toString().padStart(f.scale, '0')
      const lead = frac.length - frac.replace(/^0+/, '').length
      if (f.abs !== 0n && lead >= 8) return `${f.neg ? '−' : ''}<0.00000001`
      precision = Math.min(f.scale, Math.max(4, lead + 4))
    }
  }
  const r = rescale(f, Math.min(precision, f.scale))
  return `${signOf(r, opts.sign ?? 'auto')}${digits(r, { trimZeros: true })}`
}

export function exactTokenAmount(wei: string, decimals: number): string {
  const f = fromUnits(wei, decimals)
  return f ? `${f.neg ? '−' : ''}${digits(f, { trimZeros: true })}` : wei
}

/**
 * Percentages. `ratio: true` treats the input as a 0..1 ratio ("0.6400" →
 * "64.0%"); otherwise the input is already in percent ("25.00" → "25.0%").
 */
export function formatPct(value: string | null | undefined, opts: FormatOptions & { ratio?: boolean } = {}): string {
  if (value == null) return '—'
  let f = parseDecimal(value)
  if (!f) return '—'
  if (opts.ratio) f = f.scale >= 2 ? { ...f, scale: f.scale - 2 } : { ...f, abs: f.abs * 10n ** BigInt(2 - f.scale), scale: 0 }
  const r = rescale(f, opts.precision ?? 1)
  return `${signOf(r, opts.sign ?? 'auto')}${digits(r)}%`
}

/** Basis points → percent string: 150 → "1.50%". */
export function formatBps(bps: number, precision = 2): string {
  if (!Number.isInteger(bps)) return '—'
  return `${bps < 0 ? '−' : ''}${digits(rescale({ neg: false, abs: BigInt(Math.abs(bps)), scale: 2 }, precision))}%`
}

/** Micro-credits (1 credit = 1 000 000 micro = USD 0.01) → credits string. */
export function formatCredits(micro: string, opts: FormatOptions = {}): string {
  const f = fromUnits(micro, 6)
  if (!f) return '—'
  if (opts.compact) return `${signOf(f, opts.sign ?? 'auto')}${compactDigits(f, 0)}`
  const r = rescale(f, opts.precision ?? 0)
  return `${signOf(r, opts.sign ?? 'auto')}${digits(r)}`
}

/** Micro-credits → USD string (exact). */
export function microToUsd(micro: string): string {
  const f = fromUnits(micro, 8)
  return f ? `${f.neg ? '-' : ''}${digits(f, { group: false })}` : '0'
}

export function signum(value: string | null | undefined): -1 | 0 | 1 {
  const f = value == null ? null : parseDecimal(value)
  if (!f || f.abs === 0n) return 0
  return f.neg ? -1 : 1
}

/** Integer counts: 1290 → "1,290"; compact 12900 → "12.9k". */
/** A count; null or undefined (unknown, e.g. a trade count the platform does not serve yet) is `—`. */
export function formatCount(n: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  if (opts.compact && Math.abs(n) >= 10_000) return compactDigits({ neg: n < 0, abs: BigInt(Math.abs(Math.trunc(n))), scale: 0 }, 0)
  return group(String(Math.trunc(n)))
}

/**
 * Human decimal input ("1.5") → integer units string for `decimals`
 * ("1500000" for 6). Returns null for invalid input or too many decimals.
 */
export function toUnits(input: string, decimals: number): string | null {
  const s = input.trim().replace(/,/g, '')
  const m = /^(\d*)(?:\.(\d*))?$/.exec(s)
  if (!m || (!m[1] && !m[2])) return null
  const frac = m[2] ?? ''
  if (frac.length > decimals) return null
  const v = BigInt((m[1] || '0') + frac.padEnd(decimals, '0'))
  return v.toString()
}

/** Integer units → plain decimal string without grouping ("1500000", 6 → "1.5"). */
export function fromUnitsPlain(units: string, decimals: number): string {
  const f = fromUnits(units, decimals)
  return f ? `${f.neg ? '-' : ''}${digits(f, { group: false, trimZeros: true })}` : ''
}
